
import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useMemo
} from "react";

import { store } from "../../context/store";

import { functions } from "../../constants/common-functions";

import {
    COLOR_UTILISATION_OCCUPIED,
    OMNISCAN_CORPORATE_TURQUOIS
} from "../../constants/colors";

import {
    TRUE,
    UTILISATION_DASHBOARD_TYPE_ACTIVITY
} from "../../constants/general-constants";

import {
    CARD_STYLE,
    CHART_CONTAINER_STYLE
} from "../../constants/styles";

import {
    EMPTY_FILTER_BEAN
} from "../../constants/filter-constants";

import {
    childDashboardsUtilisation
} from "../../constants/dashboards-children";

import {
    nonStandardUtilisationUrls
} from "../../constants/client-specifics";

import {
    useHistory,
    useParams
} from "react-router-dom";

import Header from "../../components/Header";
import LeftMenu from "../../components/LeftMenu";
import DashboardSelector from "../../components/DashboardSelector";
import DashboardPhotoNavigator from "../../components/DashboardPhotoNavigator";

import AmChartSimpleBar from "../../components/AmChartSimpleBar";
import AmChartRingIndicator from "../../components/AmChartRingIndicator";
import AmChartStacked from "../../components/AmChartStacked";

import UtilisationKpiBox from "../utilisation/components/UtilisationKpiBox";
import UtilisationKpiBoxDuo from "../utilisation/components/UtilisationKpiBoxDuo";
import UtilisationKpiBoxDate from "../utilisation/components/UtilisationKpiBoxDate";

import UtilisationChartProfileUnused
    from "../utilisation/components/UtilisationChartProfileUnused";

import UtilisationChartEfficiency
    from "../utilisation/components/UtilisationChartEfficiency";

import UtilisationOrganisationNavigator
    from "../utilisation/components/UtilisationOrganisationNavigator";

import MobileUtilisationAllMeeting
    from "./mobile/MobileUtilisationAllMeeting";

import "./UtilisationAll.css";


//========================================================
// OMNISCAN PRO
// UTILISATION ALL MEETING
//
// Unified dashboard grid layout
// Shared stylesheet: UtilisationAll.css
//========================================================

export default function UtilisationAllMeeting()
{

    //----------------------------------------------------
    // Context / Routing
    //----------------------------------------------------

    const globalState =
        useContext(store);

    const history =
        useHistory();

    const refFacility =
        useRef(null);

    const {
        facilityId: facilityIdParam,
        spaceTypeId: spaceTypeIdParam
    } = useParams();

    const queryContext =
        globalState?.state?.queryContext;


    //----------------------------------------------------
    // KPI State
    //----------------------------------------------------

    const [utilisationAvg, setUtilisationAvg] =
        useState(0);

    const [utilisationMax, setUtilisationMax] =
        useState(0);

    const [seats, setSeats] =
        useState(0);

    const [workplaces, setWorkplaces] =
        useState(0);

    const [observations, setObservations] =
        useState(0);

    const [datePeak, setDatePeak] =
        useState("");

    const [dateMin, setDateMin] =
        useState("");

    const [timePeak, setTimePeak] =
        useState("");

    const [timeMin, setTimeMin] =
        useState("");


    //----------------------------------------------------
    // Chart State
    //----------------------------------------------------

    const [leftBarChartData, setLeftBarChartData] =
        useState([]);

    const [leftBarChartHeight, setLeftBarChartHeight] =
        useState("100px");

    const [dataTimeline, setDataTimeline] =
        useState([]);

    const [activityDefs, setActivityDefs] =
        useState([]);

    const [loaded, setLoaded] =
        useState(false);


    //----------------------------------------------------
    // Filter State
    //----------------------------------------------------

    const [filterBean, setFilterBean] =
        useState(EMPTY_FILTER_BEAN);

    const [facilityId, setFacilityId] =
        useState(-100);

    const [floorId, setFloorId] =
        useState(-100);

    const [floorZoneId, setFloorZoneId] =
        useState(-100);

    const [spaceId, setSpaceId] =
        useState(-100);

    const [spaceTypeId, setSpaceTypeId] =
        useState(-100);

    const [parentSpaceTypeId, setParentSpaceTypeId] =
        useState(
            spaceTypeIdParam
                ? parseInt(spaceTypeIdParam, 10)
                : -100
        );

    const [organisationId, setOrganisationId] =
        useState(-100);

    const [parentOrganisationId, setParentOrganisationId] =
        useState(-100);

    const [showCoreDayOnly, setShowCoreDayOnly] =
        useState(0);

    const [showDesksMeetings, setShowDesksMeetings] =
        useState(-100);


    //====================================================
    // INITIALISATION
    //====================================================

    //----------------------------------------------------
    // Route Space Type
    //----------------------------------------------------

    useEffect(() =>
    {
        setParentSpaceTypeId(
            spaceTypeIdParam
                ? parseInt(spaceTypeIdParam, 10)
                : -100
        );

    }, [spaceTypeIdParam]);


    //----------------------------------------------------
    // Space Type Filter
    //----------------------------------------------------

    useEffect(() =>
    {
        setFilterBean({
            ...EMPTY_FILTER_BEAN,

            spaceTypes:
                parentSpaceTypeId !== -100
                    ? [parentSpaceTypeId]
                    : []
        });

    }, [parentSpaceTypeId]);


    //----------------------------------------------------
    // Activity Types
    //----------------------------------------------------

    useEffect(() =>
    {
        functions.getArrayFromAjax(
            "/resolutionsoftwareplatform/utilisation/activity-types-used",
            handleActivityTypes,
            null
        );

    }, [parentSpaceTypeId]);


    const handleActivityTypes = (results) =>
    {
        const mapped =
            results.map((a) =>
            ({
                id:
                    String(a.id),

                title:
                    a.name,

                keyColumn:
                    String(a.id),

                color:
                    a.color || "#999"
            }));

        setActivityDefs(mapped);

        setLoaded(true);
    };


    //====================================================
    // ACTIVITY GRAPH DEFINITIONS
    //====================================================

    const graphsTimeline =
        useMemo(() =>
        {
            if(
                !activityDefs.length
                ||
                !dataTimeline.length
            )
            {
                return [];
            }

            return Object.keys(dataTimeline[0])
                .filter(
                    (k) =>
                        k !== "title"
                        &&
                        k !== "0"
                )
                .map((k) =>
                {
                    const def =
                        activityDefs.find(
                            (a) =>
                                a.id === String(k)
                        );

                    return {
                        id:
                            String(k),

                        title:
                            def
                                ? def.title
                                : "Activity " + k,

                        keyColumn:
                            String(k),

                        color:
                            def
                                ? def.color
                                : "#999"
                    };
                });

        }, [activityDefs, dataTimeline]);


    //====================================================
    // LOAD DASHBOARD DATA
    //====================================================

    useEffect(() =>
    {
        if(!loaded)
        {
            return;
        }

        if(
            nonStandardUtilisationUrls.includes(
                window.location.hostname
            )
        )
        {
            history.push(
                "/utilisationallnonstandard/-100"
            );

            return;
        }

        if(parentSpaceTypeId === -100)
        {
            return;
        }


        //------------------------------------------------
        // Shared Request Body
        //------------------------------------------------

        const requestBody =
            functions.buildPostBodyFilterBeanQueryContext(
                filterBean,
                queryContext
            );


        //------------------------------------------------
        // Average Utilisation
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationaveragemeetingroom",
            requestBody,
            populateUtilisationAvg
        );


        //------------------------------------------------
        // Peak Utilisation
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationpeakmeetingroom",
            requestBody,
            populateUtilisationMax
        );


        //------------------------------------------------
        // Capacity
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetcapacity",
            requestBody,
            populateSeats
        );


        //------------------------------------------------
        // Worksettings
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetworkplacecount",
            requestBody,
            populateWorkplaces
        );


        //------------------------------------------------
        // Observations
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetobservations",
            requestBody,
            populateObservations
        );


        //------------------------------------------------
        // Maximum Date
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetdatemax",
            requestBody,
            calculateDatePeak
        );


        //------------------------------------------------
        // Minimum Date
        //------------------------------------------------

        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetdatemin",
            requestBody,
            calculateDateMin
        );


        //------------------------------------------------
        // Breakdown
        //------------------------------------------------

        if(String(facilityId) === "-100")
        {
            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetavgbyfacility",
                requestBody,
                populateDataByFacility
            );
        }
        else if(String(floorId) === "-100")
        {
            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetavgbyfloor",
                requestBody,
                populateDataByFloor
            );
        }
        else if(String(spaceId) === "-100")
        {
            functions.postFilterBeanAjax(
                "/resolutionsoftwareplatform/utilisationstandardgetavgbyspace",
                requestBody,
                populateDataBySpace
            );
        }


        //------------------------------------------------
        // Timeline
        //------------------------------------------------

        loadDataTimeline(filterBean);

    }, [
        parentSpaceTypeId,
        facilityId,
        floorId,
        spaceId,
        spaceTypeId,
        organisationId,
        parentOrganisationId,
        showCoreDayOnly,
        showDesksMeetings,
        floorZoneId,
        filterBean,
        queryContext,
        loaded
    ]);


    //====================================================
    // TIMELINE
    //====================================================

    const loadDataTimeline = (currentFilterBean) =>
    {
        functions.postFilterBeanAjax(
            "/resolutionsoftwareplatform/utilisationstandardgetactivityovertime",

            functions.buildPostBodyFilterBeanQueryContext(
                currentFilterBean,
                queryContext
            ),

            handleDataTimeline
        );
    };


    const handleDataTimeline = (obj) =>
    {
        const cleanedData =
            obj.returnObjects.map((row) =>
            {
                const newRow = {
                    title: row.title
                };

                Object.keys(row).forEach((k) =>
                {
                    if(k === "title")
                    {
                        return;
                    }

                    if(k !== "0")
                    {
                        newRow[k] = row[k];
                    }
                });

                return newRow;
            });

        setDataTimeline(cleanedData);
    };


    //====================================================
    // KPI CALLBACKS
    //====================================================

    const populateSeats = (obj) =>
    {
        setSeats(obj.returnDouble);
    };


    const populateWorkplaces = (obj) =>
    {
        setWorkplaces(obj.returnDouble);
    };


    const populateObservations = (obj) =>
    {
        setObservations(obj.returnDouble);
    };


    const populateUtilisationAvg = (obj) =>
    {
        setUtilisationAvg(obj.returnDouble);
    };


    const populateUtilisationMax = (obj) =>
    {
        const value =
            obj.returnDouble;

        setUtilisationMax(
            parseInt(value, 10) > 100
                ? 100
                : value
        );
    };


    const calculateDatePeak = (input) =>
    {
        setTimePeak(input.hour);

        setDatePeak(input.returnDate);
    };


    const calculateDateMin = (input) =>
    {
        setTimeMin(input.hour);

        setDateMin(input.returnDate);
    };


    //====================================================
    // BREAKDOWN DATA
    //====================================================

    const populateDataByFacility = (results) =>
    {
        setLeftBarChartData(
            results.map((item) =>
            ({
                id: item.id,
                label: item.name,
                value: item.avg
            }))
        );
    };


    const populateDataByFloor = (results) =>
    {
        setLeftBarChartData(
            results.map((item) =>
            ({
                id: item.id,
                label: item.name,
                value: item.avg
            }))
        );
    };


    const populateDataBySpace = (results) =>
    {
        setLeftBarChartData(
            results.map((item) =>
            ({
                id: item.id,
                label: item.name,
                value: item.avg
            }))
        );
    };


    //----------------------------------------------------
    // Breakdown Chart Height
    //----------------------------------------------------

    useEffect(() =>
    {
        setLeftBarChartHeight(
            (20 * leftBarChartData.length + 100) + "px"
        );

    }, [leftBarChartData]);


    //====================================================
    // NAVIGATION
    //====================================================

    const clickFacility = (id) =>
    {
        if(refFacility.current)
        {
            refFacility.current.value = id;
        }

        setFacilityId(id);
    };


    const clickFloor = (id) =>
    {
        setFloorId(id);
    };


    const clickSpace = (id) =>
    {
        setSpaceId(id);
    };


    const handleBarClick = (id) =>
    {
        if(String(facilityId) === "-100")
        {
            clickFacility(id);
        }
        else if(String(floorId) === "-100")
        {
            clickFloor(id);
        }
        else
        {
            clickSpace(id);
        }
    };


    //----------------------------------------------------
    // Reset
    //----------------------------------------------------

    const resetSearch = () =>
    {
        setFacilityId(-100);
        setFloorId(-100);
        setFloorZoneId(-100);
        setSpaceId(-100);
        setSpaceTypeId(-100);
        setOrganisationId(-100);
        setParentOrganisationId(-100);
        setShowCoreDayOnly(0);
    };


    //====================================================
    // DASHBOARD LAYOUT
    //====================================================

    //----------------------------------------------------
    // Tile Constants
    //----------------------------------------------------

    const TILE_GAP =
        "8px";

    const TILE_RADIUS =
        "8px";


    //----------------------------------------------------
    // Outer Container
    //----------------------------------------------------

    const thisContainer =
    {
        display:
            "flex",

        position:
            "fixed",

        flexDirection:
            "column",

        width:
            "100%",

        minHeight:
            "100vh",

        left:
            "0px",

        top:
            "0px",

        zIndex:
            1
    };


    //----------------------------------------------------
    // Main Workspace
    //----------------------------------------------------

    const mainContainer =
    {
        position:
            "fixed",

        top:
            "0px",

        left:
            "60px",

        bottom:
            "0px",

        right:
            "0px",

        overflow:
            "auto",

        zIndex:
            0,

        paddingTop:
            "90px",

        background:
            "transparent"
    };


    //----------------------------------------------------
    // Main Dashboard Grid
    //----------------------------------------------------

    const dashboardGrid =
    {
        display:
            "grid",

        gridTemplateColumns:
            "419px minmax(0, 1fr)",

        gap:
            TILE_GAP,

        width:
            "calc(100% - 40px)",

        margin:
            "20px",

        alignItems:
            "stretch"
    };


    //----------------------------------------------------
    // Left Analysis Tile
    //----------------------------------------------------

    const leftContainer =
    {
        display:
            "flex",

        flexDirection:
            "column",

        minWidth:
            0,

        minHeight:
            "calc(100vh - 130px)",

        borderTopLeftRadius:
            TILE_RADIUS,

        borderBottomLeftRadius:
            TILE_RADIUS
    };


    //----------------------------------------------------
    // Breakdown Bar Chart
    //----------------------------------------------------

    const barChartContainer =
    {
        display:
            "flex",

        width:
            "100%",

        minHeight:
            "200px",

        flex:
            1
    };


    //----------------------------------------------------
    // Right Dashboard Grid
    //----------------------------------------------------

    const rightGrid =
    {
        display:
            "grid",

        gridTemplateColumns:
            "repeat(4, minmax(0, 1fr))",

        gridTemplateRows:
            "180px minmax(240px, auto) minmax(300px, auto)",

        gap:
            TILE_GAP,

        minWidth:
            0,

        alignItems:
            "stretch"
    };


    //----------------------------------------------------
    // Doughnut Tiles
    //----------------------------------------------------

    const doughnutTile =
    {
        display:
            "flex",

        flexDirection:
            "column",

        alignItems:
            "center",

        justifyContent:
            "center",

        minWidth:
            0
    };


    //----------------------------------------------------
    // KPI Tile
    //----------------------------------------------------

    const boxIndicatorContainer =
    {
        display:
            "flex",

        flexWrap:
            "wrap",

        alignContent:
            "center",

        justifyContent:
            "center",

        columnGap:
            "20px",

        rowGap:
            "8px",

        minWidth:
            0,

        padding:
            "8px",

        boxSizing:
            "border-box"
    };


    //----------------------------------------------------
    // Middle Chart Tiles
    //----------------------------------------------------

    const middleChartTile =
    {
        display:
            "flex",

        flexDirection:
            "column",

        minWidth:
            0,

        minHeight:
            "240px",

        paddingTop:
            "10px"
    };


    //----------------------------------------------------
    // Timeline Tile
    //----------------------------------------------------

    const timelineTile =
    {
        display:
            "flex",

        flexDirection:
            "column",

        minWidth:
            0,

        minHeight:
            "300px",

        borderBottomRightRadius:
            TILE_RADIUS
    };


    //----------------------------------------------------
    // Chart Title
    //----------------------------------------------------

    const chartTitle =
    {
        display:
            "flex",

        width:
            "100%",

        height:
            "20px",

        fontSize:
            "12px",

        fontWeight:
            "bold",

        alignItems:
            "center",

        justifyContent:
            "space-around",

        paddingTop:
            "10px"
    };


    //====================================================
    // MOBILE
    //====================================================

    const isMobile =
        window.innerWidth <= 768;

    if(isMobile)
    {
        return (
            <MobileUtilisationAllMeeting/>
        );
    }


    //====================================================
    // RENDER
    //====================================================

    return (

        <div style={thisContainer}>

            <Header
                transitionHide={true}
                showSurveySelector={true}
            />

            <LeftMenu/>

            <DashboardSelector
                title="Utilisation Dashboard"
                childDashboards={childDashboardsUtilisation}
            />


            {/* ============================================
                MAIN DASHBOARD
            ============================================ */}

            <div
                className="utilisation-all-main"
                style={mainContainer}
            >

                <div style={dashboardGrid}>


                    {/* ------------------------------------
                        LEFT ANALYSIS TILE
                    ------------------------------------ */}

                    <div
                        className="utilisation-all-tile"
                        style={{
                            ...CARD_STYLE,
                            ...leftContainer
                        }}
                    >

                        <div style={barChartContainer}>

                            <div style={CHART_CONTAINER_STYLE}>

                                <AmChartSimpleBar
                                    chartId="utilisationallleftbar"
                                    chartData={leftBarChartData}
                                    categoryKey="label"
                                    valueKey="value"
                                    width="100%"
                                    height={leftBarChartHeight}
                                    onClick={handleBarClick}
                                    color={COLOR_UTILISATION_OCCUPIED}
                                />

                            </div>

                        </div>


                        <div style={chartTitle}>

                            Breakdown by Building/Floor/Workplace

                        </div>

                    </div>


                    {/* ====================================
                        RIGHT DASHBOARD GRID
                    ==================================== */}

                    <div style={rightGrid}>


                        {/* -------------------------------
                            AVERAGE UTILISATION
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...doughnutTile
                            }}
                        >

                            <div
                                style={{
                                    ...CHART_CONTAINER_STYLE,

                                    height:
                                        "140px",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center"
                                }}
                            >

                                <AmChartRingIndicator
                                    percentage={utilisationAvg}
                                    width={130}
                                    height={130}
                                    icon="chair"
                                    color={COLOR_UTILISATION_OCCUPIED}
                                    label="Avg"
                                    id="utilisationmeetingdoughnutavg"
                                />

                            </div>

                        </div>


                        {/* -------------------------------
                            PEAK UTILISATION
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...doughnutTile
                            }}
                        >

                            <div
                                style={{
                                    ...CHART_CONTAINER_STYLE,

                                    height:
                                        "140px",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center"
                                }}
                            >

                                <AmChartRingIndicator
                                    percentage={utilisationMax}
                                    width={130}
                                    height={130}
                                    icon="chair"
                                    color={COLOR_UTILISATION_OCCUPIED}
                                    label="Peak"
                                    id="utilisationmeetingdoughnutmin"
                                />

                            </div>

                        </div>


                        {/* -------------------------------
                            KPI SUMMARY
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...boxIndicatorContainer,

                                gridColumn:
                                    "3 / 5"
                            }}
                        >

                            <UtilisationKpiBoxDuo
                                title1="Seats"
                                title2="Worksettings"
                                figure1={
                                    functions.formatNumber(
                                        seats,
                                        0
                                    )
                                }
                                figure2={
                                    functions.formatNumber(
                                        workplaces,
                                        0
                                    )
                                }
                                color={OMNISCAN_CORPORATE_TURQUOIS}
                                width="calc(50% - 30px)"
                            />


                            <UtilisationKpiBox
                                title="Observations"
                                figure={
                                    functions.formatNumber(
                                        observations,
                                        0
                                    )
                                }
                                color={OMNISCAN_CORPORATE_TURQUOIS}
                                width="calc(50% - 30px)"
                            />


                            <UtilisationKpiBoxDate
                                title="Max On"
                                figure={
                                    functions.formatDate(
                                        datePeak
                                    )
                                }
                                hour={timePeak}
                                width="calc(50% - 30px)"
                            />


                            <UtilisationKpiBoxDate
                                title="Min On"
                                figure={
                                    functions.formatDate(
                                        dateMin
                                    )
                                }
                                hour={timeMin}
                                width="calc(50% - 30px)"
                            />

                        </div>


                        {/* -------------------------------
                            MEETING ROOM PROFILE
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...middleChartTile,

                                gridColumn:
                                    "1 / 3"
                            }}
                        >

                            <UtilisationChartProfileUnused
                                id="utilisationchartprofileunusedmeeting"
                                filterBean={filterBean}
                                isMeeting={TRUE}
                                parentSpaceTypeId={parentSpaceTypeId}
                            />

                        </div>


                        {/* -------------------------------
                            MEETING ROOM EFFICIENCY
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...middleChartTile,

                                gridColumn:
                                    "3 / 5"
                            }}
                        >

                            <UtilisationChartEfficiency
                                id="utilisationchartefficiencydesks"
                                filterBean={filterBean}
                                isMeeting={TRUE}
                            />

                        </div>


                        {/* -------------------------------
                            ACTIVITY TIMELINE
                        ------------------------------- */}

                        <div
                            className="utilisation-all-tile"
                            style={{
                                ...CARD_STYLE,
                                ...timelineTile,

                                gridColumn:
                                    "1 / 5"
                            }}
                        >

                            <div style={CHART_CONTAINER_STYLE}>

                                {
                                    dataTimeline
                                    &&
                                    dataTimeline.length > 0
                                    &&
                                    graphsTimeline
                                    &&
                                    graphsTimeline.length > 0
                                    &&
                                    (
                                        <AmChartStacked
                                            chartId="utilisationactivitybyorganisation"
                                            width="calc(100% - 20px)"
                                            height="260px"
                                            chartData={dataTimeline}
                                            chartKeys={graphsTimeline}
                                            categoryKey="title"
                                            legendPosition="bottom"
                                        />
                                    )
                                }

                            </div>


                            <div
                                style={{
                                    ...chartTitle,

                                    marginTop:
                                        "-5px",

                                    paddingBottom:
                                        "10px"
                                }}
                            >

                                Utilisation Profile

                            </div>

                        </div>


                    </div>

                </div>

            </div>


            {/* ============================================
                ORGANISATION NAVIGATOR
            ============================================ */}

            <UtilisationOrganisationNavigator
                dashboardType={
                    UTILISATION_DASHBOARD_TYPE_ACTIVITY
                }
                selectedParentSpaceTypeId={
                    parentSpaceTypeId
                }
            />


            {/* ============================================
                PHOTO / FILTER NAVIGATOR
            ============================================ */}

            <DashboardPhotoNavigator
                refFacility={refFacility}

                facilityId={facilityId}
                setFacilityId={setFacilityId}

                floorId={floorId}
                setFloorId={setFloorId}

                floorZoneId={floorZoneId}
                setFloorZoneId={setFloorZoneId}

                spaceId={spaceId}
                setSpaceId={setSpaceId}

                spaceTypeId={spaceTypeId}
                setSpaceTypeId={setSpaceTypeId}

                parentSpaceTypeId={parentSpaceTypeId}
                setParentSpaceTypeId={setParentSpaceTypeId}

                organisationId={organisationId}
                setOrganisationId={setOrganisationId}

                parentOrganisationId={parentOrganisationId}
                setParentOrganisationId={setParentOrganisationId}

                showCoreDayOnly={showCoreDayOnly}
                setShowCoreDayOnly={setShowCoreDayOnly}

                showDesksMeetings={showDesksMeetings}
                setShowDesksMeetings={setShowDesksMeetings}

                resetFunction={resetSearch}
                setFilterBean={setFilterBean}
            />

        </div>

    );

}
